/// <amd-module name="VSS/Features/Directory/components/DirectoryGrid/DirectoryGrid" />
import "VSS/LoaderPlugins/Css!VSS/Features/Directory/Scripts/Common/Directory/Components/DirectoryGrid";

import { NameCell } from "./NameCell";
import { DirectoryColumnKeys, DirectoryCSS } from "./DirectoryGridConstants";
import { GroupRow } from "./GroupRow";
import { LoadingRow } from "./LoadingRow";
import { ZeroDataRow } from "./ZeroDataRow";
import { IDirectoryArtifact } from "../../DirectoryContracts";
import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import { ConstrainMode, DetailsListLayoutMode, DetailsRow, IColumn, SelectionMode } from "OfficeFabric/DetailsList";
import { Selection } from "OfficeFabric/Selection";
import { css } from "VSSUI/Util";
import * as React from "react";
import { VssDetailsList } from "VSSUI/Components/VssDetailsList/VssDetailsList";
import { IVssContextualMenuItemProvider } from "VSSUI/VssContextualMenu";
import { IDirectoryRow, IGridRow, IGroupRow, IZeroDataRow, ILoadingRow, IGridRowProps } from "./DirectoryGrid.Props";

export interface IDirectoryGridProps<T extends IDirectoryArtifact> {
    /** Optional class name to append to the Grid container */
    className?: string;
    /** Get context menu items for the item */
    getContextMenuItems?: (item: IDirectoryRow<T>) => IContextualMenuItem[];
    /** Adds additional items to context menu. Usually used for async retrieval of contributed menu items. */
    getContextMenuItemProviders?: (activatedItem: any) => IVssContextualMenuItemProvider[];
    /** Get the content for a zero data row for a group */
    getZeroDataContent?: (groupKey: string) => { content: JSX.Element | string; ariaLabel?: string };
    /** The groups to display */
    rows: IGridRow<T>[];
    isGrouped: boolean;
    /** The columns to display */
    columns: IColumn[];
    /** Optional render override */
    onRenderCell?: (
        item: IDirectoryRow<T>,
        index: number,
        column: IColumn,
        defaultRenderer?: (item: IDirectoryRow<T>, index: number, column: IColumn) => JSX.Element
    ) => JSX.Element;

    /** Handler which should render a zero data row for a group */
    onRenderZeroDataRow?: (groupKey: string) => JSX.Element | undefined;

    /** Called when a group is expanded/collapsed */
    onGroupToggled?: (groupKey: string, collapsed: boolean) => void;
    /** Called when an item link is clicked/activated */
    onItemClicked?: (item: IDirectoryRow<T>, e?: Event) => void;
}

/**
 * A generic directory grid component
 * @param T The type of the underlying artifact (ex: Team)
 * @param R The type of the row object, which extends IDirectoryRow<T>
 */
export class DirectoryGrid<T extends IDirectoryArtifact> extends React.Component<IDirectoryGridProps<T>> {
    private _selection: Selection = new Selection();
    private _prevSelectedIndex: number | undefined;
    private _detailsList:VssDetailsList;

    public render(): JSX.Element {
        const { className, columns, rows, getContextMenuItems, getContextMenuItemProviders } = this.props;

         this._updatePrevSelectedIndex();

        return (
            <div className={css("new-directory-grid-container", className)}>
                <VssDetailsList
                    ref={(lst: any) => {this._detailsList = lst;}}
                    setKey={"directory-gridview"} // setKey is required to keep focus on the selected item when the row is re-rendered
                    layoutMode={DetailsListLayoutMode.justified}
                    constrainMode={ConstrainMode.unconstrained}
                    isHeaderVisible={true}
                    columns={columns}
                    className="directory-gridview"
                    items={rows}
                    listProps={{
                        getPageSpecification: this._getPageSpecification
                    }}
                    onRenderItemColumn={this._onRenderCell}
                    selectionMode={SelectionMode.single}
                    selection={this._selection}
                    getKey={this._getRowKey}
                    onRenderRow={this._onRenderRow}
                    selectionPreservedOnEmptyClick={true}
                    allocateSpaceForActionsButtonWhileHidden={true}
                    actionsColumnKey={DirectoryColumnKeys.Name}
                    getMenuItems={getContextMenuItems ? this._getContextMenuItems : undefined}
                    getMenuItemProviders={getContextMenuItemProviders}
                    shouldDisplayActions={this._shouldDisplayActions}
                    onItemInvoked={this._onRowInvoked}
                />
            </div>
        );
    }

    public componentDidUpdate(prevProps: IDirectoryGridProps<T>): void {
        // update selection if row count reduces and a selection which was present before the render is now gone.
        if (this.props.rows.length < prevProps.rows.length && this._selection.getSelection().length === 0 && this._prevSelectedIndex !== undefined) {
            this._updateSelection();
        }
    }

    private _updatePrevSelectedIndex(): void {
        const selectedIndices = this._selection.getSelectedIndices();
        // remember the selected index, as we will lose it after rendering if the index gets deleted.
        this._prevSelectedIndex = selectedIndices.length !== 0 ? selectedIndices[0] : undefined;
    }

    private _updateSelection(): void {
        if (this._prevSelectedIndex === undefined) {
            return;
        }

        const { rows } = this.props;
        const indexToFocus = rows.length <= this._prevSelectedIndex ? rows.length - 1 : this._prevSelectedIndex;

        // select the row and then focus it.
        this._selection.setIndexSelected(indexToFocus, true, false);
        this._detailsList.detailsList.focusIndex(indexToFocus);
    }

    private _onRenderRow = (props: IGridRowProps): JSX.Element => {
        const { columns, isGrouped, onRenderZeroDataRow } = this.props;

        if (this._isGroupRow(props.item)) {
            return <GroupRow columnKey={columns[0].key} row={props.item} onToggled={this._onGroupRowToggled} {...props} />;
        } else if (this._isZeroDataRow(props.item)) {
            return (
                <ZeroDataRow columnKey={columns[0].key} isGrouped={isGrouped} onRenderRowContent={onRenderZeroDataRow} row={props.item} {...props} />
            );
        } else if (this._isLoadingRow(props.item)) {
            return <LoadingRow columnKey={columns[0].key} isGrouped={isGrouped} row={props.item} {...props} />;
        } else {
            return <DetailsRow {...props} onRenderItemColumn={this._onRenderCell} />;
        }
    };

    private _getPageSpecification = (index: number, visibleRect: any) => {
        const { isGrouped, rows } = this.props;

        const maxPageSize = 10;
        if (isGrouped) {
            const startRow = rows[index];
            if (startRow) {
                let itemsOnPage = 0;

                let currentGroupKey = this._getRowGroupKey(startRow);
                if (!currentGroupKey) {
                    // throw
                }

                const groupItems = rows.filter(r => this._getRowGroupKey(r) === currentGroupKey);
                const groupItemIndex = groupItems.findIndex(r => r.key === startRow.key);

                for (let i = index; itemsOnPage < maxPageSize && i < rows.length; i++) {
                    const row = rows[i];
                    const rowGroupKey = this._getRowGroupKey(row);
                    if (rowGroupKey !== currentGroupKey) {
                        // End page
                        break;
                    }

                    itemsOnPage++;
                }

                return {
                    itemCount: itemsOnPage,
                    height: itemsOnPage * 36,
                    data: null,
                    key: `p-${currentGroupKey}-${groupItemIndex}`
                };
            }
        }

        return {
            itemCount: maxPageSize,
            height: maxPageSize * 36,
            data: null,
            key: `p-${index}`
        };
    };

    private _onRenderCell = (row: IDirectoryRow<T>, index: number, column: IColumn): JSX.Element | undefined => {
        const { columns, isGrouped, onRenderCell = this._defaultOnRenderCell } = this.props;

        // Even though each row specifies their own default renderer, those renderers need to be duplicated here
        // for directories that want to use context menus. VssDetailsList forces rendering of the cell where the
        // context menu has been specified to go (defined as the name column here) to call this function, overriding
        // any defaults they may have specified. So we include those renderers here to make sure their desired behavior
        // is followed.
        if (this._isGroupRow(row)) {
            return GroupRow.RenderCell(row, index, column, this.props.columns[0].key);
        } else if (this._isLoadingRow(row)) {
            return LoadingRow.RenderCell(row, index, column, this.props.columns[0].key, isGrouped);
        } else if (this._isZeroDataRow(row)) {
            const { onRenderZeroDataRow } = this.props;
            return ZeroDataRow.RenderCell(row, index, column, this.props.columns[0].key, isGrouped, onRenderZeroDataRow);
        }

        return (
            <div className={DirectoryCSS.DIRECTORY_ROWITEM}>
                {isGrouped && column.key === columns[0].key && <div className={DirectoryCSS.DIRECTORY_GROUP_SPACER} />}
                {onRenderCell(row, index, column, this._defaultOnRenderCell)}
            </div>
        );
    };

    private _getRowGroupKey(row: IGridRow<T>): string | undefined {
        if (this._isGroupRow(row)) {
            return (row as IGroupRow).key;
        }

        return (row as IDirectoryRow<T>).groupKey;
    }

    private _defaultOnRenderCell = (row: IDirectoryRow<T>, index: number, column: IColumn, __defaultRenderer: any): JSX.Element | null => {
        switch (column.fieldName) {
            case DirectoryColumnKeys.Name:
                return this._renderNameCell(row, index);
            default:
                return null;
        }
    };

    private _renderNameCell(row: IDirectoryRow<T>, index: number): JSX.Element {
        return <NameCell row={row} onClick={this._onRowClicked} />;
    }

    private _getContextMenuItems = (row: IGridRow<T>): IContextualMenuItem[] => {
        const { getContextMenuItems } = this.props;

        if (getContextMenuItems && !this._isGroupRow(row) && !this._isZeroDataRow(row) && !this._isLoadingRow(row)) {
            return getContextMenuItems(row as IDirectoryRow<T>);
        }

        return [];
    };

    private _onGroupRowToggled = (row: IGroupRow, collapsed: boolean): void => {
        const { onGroupToggled } = this.props;

        if (onGroupToggled) {
            onGroupToggled(row.key, collapsed);
        }
    };

    private _onRowClicked = (row: IDirectoryRow<T>, index?: number, ev?: Event): void => {
        const { onItemClicked } = this.props;

        if (onItemClicked) {
            onItemClicked(row, ev);
        }
    };

    private _onRowInvoked = (row: IGridRow<T>, index?: number, ev?: Event): void => {
        if (!this._isGroupRow(row)) {
            const directoryRow = row as IDirectoryRow<T>;
            if (!directoryRow.isDeleted) {
                this._onRowClicked(row as IDirectoryRow<T>, index, ev);
            }
        }
    };

    private _getRowKey = (row: IDirectoryRow<T>): string => {
        return row.key;
    };

    private _isZeroDataRow(row: IGridRow<T>): row is IZeroDataRow {
        return row.hasOwnProperty("isZeroDataRow") && (row as IZeroDataRow).isZeroDataRow;
    }

    private _isGroupRow(row: IGridRow<T>): row is IGroupRow {
        return row.hasOwnProperty("isGroupRow") && (row as IGroupRow).isGroupRow;
    }

    private _isLoadingRow(row: IGridRow<T>): row is ILoadingRow {
        return row.hasOwnProperty("isLoadingRow") && (row as ILoadingRow).isLoadingRow;
    }

    private _shouldDisplayActions = (row: IGridRow<T>): boolean => {
        const { getContextMenuItems } = this.props;

        if (!getContextMenuItems) {
            return false;
        }

        if (this._isGroupRow(row) || this._isLoadingRow(row) || this._isZeroDataRow(row)) {
            return false;
        }

        return true;
    };
}
